/* import __COLOCATED_TEMPLATE__ from './lsg-user-message-bubble.hbs'; */
import { equal, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { later } from '@ember/runloop';
import Component from '@ember/component';
import { stripTags } from 'sh-www/utils/misc';
import { showModal } from "sh-www/utils/modal-controls";
import { THREAD_MESSAGE_STRIPTAGS_EXCEPTIONS } from 'sh-www/constants/messages';
import clsx from 'clsx';

/**
 * What side of the text do we want to show the image bubble
 *
 * @type {Object}
 */
export const LSG_USER_MESSAGE_BUBBLE__BUBBLE_LOCATION = {
  LEFT: 'left',
  RIGHT: 'right'
};

/**
 * @type {Object}     The available time modes we can display the time in.
 */
export const LSG_USER_MESSAGE_BUBBLE__TIME_MODES = {
  ABSOLUTE: 'absolute', // GMT time
  RELATIVE: 'relative' // relative 'days ago' time
};

/**
 * The amount of time in ms before we re-render the relative time for a message bubble.
 *
 * @type {number}
 */
const LSG_USER_MESSAGE_BUBBLE__RE_RENDER_RELATIVE_TIME_IN_MS = 120000;

/**
 * This is a representation of a message from a thread in the newer lsg format
 */
export default Component.extend({
  session: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['LSG-c-user-message-bubble'],
  classNameBindings: ['_isUnread:is-unread'],

  /**
   * Register services
   */

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /** @param {Date} lastReadAt When the thread was last seen by the user. */
  lastReadAt: null,

  /**
   * @param {Message|null}    The message to render
   */
  message: null,

  /**
   * @type {boolean}
   */
  shouldObfuscate: false,

  /** @param {Function} onClick The function to call when the message is clicked. */
  onClick: () => {},

  /** @param {Function} onProfileLinkClick */
  onProfileLinkClick: () => {},

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /**
   * @param {Boolean}     Set this to true if the avatar image should be linkable to that user slug.
   */
  isImageLinkable: false,

  /**
   * A function to handle the case where the time mode is toggled.  If not handled, it would just toggle the instance
   *
   * @param Function      modeSelected {LSG_USER_MESSAGE_BUBBLE__TIME_MODES.*}
   */
  onTimeModeToggled: function(modeSelected) {},

  /**
   * Set the time mode to one of the supported time modes
   *
   * @param {String}    of type LSG_USER_MESSAGE_BUBBLE__TIME_MODES.*
   */
  timeMode: LSG_USER_MESSAGE_BUBBLE__TIME_MODES.RELATIVE,

  /**
   * What side of the text do we want to put the image.
   *
   * @param {String=}     of type LSG_USER_MESSAGE_BUBBLE__BUBBLE_LOCATION.*
   */
  userImageBubbleLocation: LSG_USER_MESSAGE_BUBBLE__BUBBLE_LOCATION.LEFT,

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  click() {
    this.get('onClick')();
  },

  /**
   * @inheritDoc
   */
  didInsertElement() {
    let toggleRelativeTimeTrigger = () => {
      if (!this.get || this.get('isDestroyed')) {
        // stop the timer after the component has been removed from the page.
        return;
      }

      this.toggleProperty('_reComputeRelativeTimeTrigger');

      later(this, function() {
        toggleRelativeTimeTrigger();
      }, LSG_USER_MESSAGE_BUBBLE__RE_RENDER_RELATIVE_TIME_IN_MS);
    };

    later(this, function() {
      toggleRelativeTimeTrigger();
    }, LSG_USER_MESSAGE_BUBBLE__RE_RENDER_RELATIVE_TIME_IN_MS);
  },

  /*********************************
   * PRIVATE
   **********************************/

  /** @type {String} Sanitized thread message. */
  _messageBodySanitized: computed('message.body', function () {
    return stripTags(this.message.body, THREAD_MESSAGE_STRIPTAGS_EXCEPTIONS);
  }),

  /** @type {Boolean} Whether the message is unread. */
  _isUnread: computed('lastReadAt', 'message.createdAt', function () {
    if (!this.get('lastReadAt')) return false;

    return this.get('lastReadAt').getTime() < this.get('message.createdAt').getTime();
  }),

  /**
   * @type {Boolean}    True if the current user can flag the current message.
   */
  _canFlagMessage: computed(
  'currentUser.isBuyer',
  'message.user.isBuyer',
  'message.user.isSeller',
  function() {
    if (this.get('currentUser.isBuyer')) {
      // buyers can only flag SELLERS and NOT ADMINS
      return this.get('message.user.isSeller') && !this.get('message.user.isAdmin');
    }

    // current user IS a SELLER at this point, so can only flag if the other user is a buyer and NOT an admin.
    return this.get('message.user.isBuyer') && !this.get('message.user.isAdmin');
  }),

  /**
   * @type {Boolean}      True when we are in the absolute time mode
   */
  _isAbsoluteTimeMode: computed('timeMode', function() {
    return this.get('timeMode') === LSG_USER_MESSAGE_BUBBLE__TIME_MODES.ABSOLUTE;
  }),

  /**
   * @type {Boolean}    True if image bubble is on left of text.
   */
  _isMessageBubbleLeft: equal('userImageBubbleLocation', LSG_USER_MESSAGE_BUBBLE__BUBBLE_LOCATION.LEFT),

  /**
   * This will trigger a redraw of the relative timestamp on a certain interval.  All we are doing is toggling
   * this back and forth to trigger a redraw in Ember.
   *
   * @type {Boolean}
   */
  _reComputeRelativeTimeTrigger: false,

  /**
   * @type {string}
   */
  bubbleContentClassNames: computed('shouldObfuscate', function() {
    return clsx(
      'LSG-c-user-message-bubble__content',
      this.shouldObfuscate && 'LSG-c-user-message-bubble__content--obfuscated',
    );
  }),

  /**
   * @inheritDoc
   */
  actions: {
    /**
     * @param {'user'|'company'} type
     * @param {User|Company} model
     */
    handleProfileLinkClick(type, model) {
      this.get('onProfileLinkClick')(type, model);
    },

    /**
     * Called when the user has clicked flag message
     */
    onFlagMessage: function() {
      let threadId = this.get('message.thread.id'),
        modalModel = {
          threadId: threadId
        };

      showModal(this, 'flag-user-modal', modalModel);
    },

    /**
     * Called when user clicks to toggle the time mode
     */
    onTimeModeToggled: function(e) {
      e.stopPropagation();
      this.onTimeModeToggled();
    },
  }
});
